import { useState } from "react";
import { AuthContainer } from "./AuthContainer";
import { useAuth } from "./AuthContext";
import { apiPost } from "../services/api.service";
import { showError, showSuccess } from "../services/notification.service";
import { useTranslation } from "../i18n/TranslationContext";

export function ForgotPassword() {
	const { t } = useTranslation();
	const { setCurrentStep } = useAuth();
	const [email, setEmail] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!email) {
			showError(t("forgotPassword.emailRequired"));
			return;
		}

		setIsLoading(true);

		try {
			const response = await apiPost(
				"/auth/forgot-password",
				{ email },
				{
					notification: { enabled: false },
				}
			);

			if (response.success) {
				setIsSubmitted(true);
				showSuccess(t("forgotPassword.resetEmailSent"));
			} else {
				showError(response.message || t("forgotPassword.failedToSend"));
			}
		} catch (error: any) {
			console.error("Forgot password error:", error);
			showError(error.message || t("forgotPassword.errorOccurred"));
		} finally {
			setIsLoading(false);
		}
	};

	const handleBack = () => {
		setCurrentStep(6); // Go back to password login
	};

	if (isSubmitted) {
		return (
			<AuthContainer showBack={false} showProgress={false}>
				<div className="text-center space-y-6">
					<div>
						<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-2 leading-tight">
							{t("forgotPassword.successTitle")}
						</h1>
						<p className="text-[var(--ucm-text-medium)]">
							{t("forgotPassword.successSubtitle")}
						</p>
						<p className="font-medium text-[var(--ucm-text-dark)] mt-2">
							{email}
						</p>
					</div>

					<div className="space-y-4 pt-4">
						<p className="text-sm text-[var(--ucm-text-light)]">
							{t("forgotPassword.successMessage")}
						</p>
						
						<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-lg p-3">
							<p className="text-xs text-[var(--ucm-dark-blue)]">
								{t("forgotPassword.spamFolderNote")}
							</p>
						</div>

						<button
							onClick={() => setCurrentStep(0)}
							className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
						>
							{t("forgotPassword.backToSignIn")}
						</button>

						<p className="text-sm text-center text-[var(--ucm-text-light)]">
							{t("forgotPassword.didntReceiveEmail")}{" "}
							<button
								onClick={() => {
									setIsSubmitted(false);
									setEmail("");
								}}
								className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] hover:underline"
							>
								{t("forgotPassword.tryAgain")}
							</button>
						</p>
					</div>
				</div>
			</AuthContainer>
		);
	}

	return (
		<AuthContainer showBack onBack={handleBack} showProgress={false}>
			<div className="text-center mb-8">
				<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-2 leading-tight">
					{t("forgotPassword.title")}
				</h1>
				<p className="text-[var(--ucm-text-medium)]">
					{t("forgotPassword.subtitle")}
				</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				<div>
					<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
						{t("forgotPassword.emailLabel")}
					</label>
					<input
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
						placeholder={t("forgotPassword.emailPlaceholder")}
						required
						autoComplete="email"
						disabled={isLoading}
						autoFocus
					/>
				</div>

				<button
					type="submit"
					disabled={isLoading || !email}
					className={`w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2 ${
						isLoading || !email
							? "opacity-50 cursor-not-allowed"
							: "hover:bg-[var(--ucm-primary-teal-hover)]"
					}`}
				>
					{isLoading ? (
						<div className="flex items-center justify-center">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
							{t("forgotPassword.sending")}
						</div>
					) : (
						t("forgotPassword.sendResetLink")
					)}
				</button>

				<p className="text-sm text-center text-[var(--ucm-text-light)]">
					{t("forgotPassword.rememberPassword")}{" "}
					<button
						type="button"
						onClick={() => setCurrentStep(0)}
						className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] hover:underline"
					>
						{t("signIn.title")}
					</button>
				</p>
			</form>
		</AuthContainer>
	);
}
